import { Footer } from "@components/Footer";
import { HowBettingWorksModal } from "@components/HowBettingWorksModal";
import { Input } from "@components/Input";
import { doc, setDoc, Timestamp, updateDoc } from "@firebase/firestore";
import { useTranslatedKey } from "@helpers/useTranslatedKey";
import { useTranslation } from "@helpers/useTranslation";
import { FC, useContext, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { FirebaseContext } from "src/helpers/firebase";
import { useConfetti } from "src/helpers/useConfetti";
import { useEventNavigate } from "src/helpers/useEventNavigate";
import { useTeam } from "src/helpers/useTeam";
import { useToast } from "src/helpers/useToast";

type Props = {
  challenge: BettingChallenge;
  solution: BettingSolution;
  teamId: string;
  eventId: string;
};

type BettingFormData = Record<string, {
  selectedOption: number;
  amount: number;
}> & {
  paymentUrl: string;
  email: string;
};

export const Betting: FC<Props> = (props) => {
  const { id, scenarios } = props.challenge;
  const { solution, eventId, teamId } = props;
  const { t } = useTranslation("challenges");
  const { t: tNewteam } = useTranslation("newteam");
  const { firestore } = useContext(FirebaseContext);
  const navigate = useEventNavigate();
  const [team] = useTeam();
  const [submitting, setSubmitting] = useState(false);
  const [showBettingModal, setShowBettingModal] = useState(false);
  useConfetti("submit-button");
  const toast = useToast();

  const { getTranslatedValue } = useTranslatedKey();
  const title = getTranslatedValue(props.challenge, "title");
  const about = getTranslatedValue(props.challenge, "about");

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<BettingFormData>({
    defaultValues: {
      paymentUrl: team?.paymentUrl || "",
      email: team?.email || "",
    } as Partial<BettingFormData>,
  });

  // Get existing bets for default values
  const existingBets = useMemo(() => {
    if (!solution?.bets) return {};
    const betsMap: { [key: string]: BettingBet } = {};
    solution.bets.forEach((bet) => {
      betsMap[bet.scenarioId] = bet;
    });
    return betsMap;
  }, [solution?.bets]);

  const onSubmit = async (data: BettingFormData) => {
    if (!team) {
      toast(t("problemOccured"), "🤷‍♂️", "error");
      return;
    }

    setSubmitting(true);

    try {
      // Convert form data to bets array
      const bets: BettingBet[] = scenarios.map((scenario) => {
        const scenarioData = data[scenario.id || ""];
        return {
          scenarioId: scenario.id || "",
          selectedOption: Number(scenarioData?.selectedOption) || 0,
          amount: Number(scenarioData?.amount) || 0,
        };
      });

      // Save betting solution
      const solutionDoc = doc(firestore, `events/${eventId}/teams/${teamId}/solutions/${id}`);
      const solutionData: BettingSolution = {
        id,
        completed: true,
        completedOn: Timestamp.now(),
        bets,
      };

      await setDoc(solutionDoc, solutionData);

      // Update team with payment URL and email
      const teamDocRef = doc(firestore, `events/${eventId}/teams/${teamId}`);
      await updateDoc(teamDocRef, {
        paymentUrl: data.paymentUrl,
        email: data.email,
      });

      toast(t("betsPlaced"), "🎰", "success");
      navigate("/challenges");
    } catch (error) {
      console.error("Error submitting bets:", error);
      toast(t("problemOccured"), "🤷‍♂️", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const watchedValues = watch();

  // Calculate total betting amount from form values
  const totalAmount = useMemo(() => {
    return scenarios.reduce((total, scenario) => {
      const scenarioData = watchedValues[scenario.id || ""];
      const amount = Number(scenarioData?.amount) || 0;
      return total + amount;
    }, 0);
  }, [watchedValues, scenarios]);

  // Check if all bets are complete (both option selected and amount entered)
  const allBetsComplete = useMemo(() => {
    return scenarios.every((scenario) => {
      const scenarioData = watchedValues[scenario.id || ""];
      const hasSelectedOption = scenarioData?.selectedOption !== undefined && scenarioData?.selectedOption !== null;
      const hasAmount = Number(scenarioData?.amount) > 0;
      return hasSelectedOption && hasAmount;
    });
  }, [watchedValues, scenarios]);

  // Check if payment fields are filled
  const paymentFieldsFilled = useMemo(() => {
    const paymentUrl = watchedValues.paymentUrl;
    const email = watchedValues.email;
    return !!paymentUrl && paymentUrl.trim() !== "" && !!email && email.trim() !== "";
  }, [watchedValues.paymentUrl, watchedValues.email]);

  // Check if form is ready to submit
  const canSubmit = useMemo(() => {
    return allBetsComplete && paymentFieldsFilled;
  }, [allBetsComplete, paymentFieldsFilled]);

  // Generate tooltip message
  const submitTooltip = useMemo(() => {
    if (!allBetsComplete && !paymentFieldsFilled) {
      return t("mustPlaceAllBetsAndPaymentInfo");
    }
    if (!allBetsComplete) {
      return t("mustPlaceAllBets");
    }
    if (!paymentFieldsFilled) {
      return t("mustProvidePaymentInfo");
    }
    return "";
  }, [allBetsComplete, paymentFieldsFilled, t]);

  // Calculate total betting amount from completed solution
  const completedTotalAmount = useMemo(() => {
    if (!solution?.bets) return 0;
    return solution.bets.reduce((sum, bet) => sum + Number(bet.amount), 0);
  }, [solution?.bets]);

  // Check if betting is closed
  const isBettingClosed = props.challenge.bettingsClosed;

  // If betting is closed and user hasn't placed bets, show results
  if (isBettingClosed && !solution?.completed) {
    const bettingResults = props.challenge.bettingResults || [];
    const paymentTransactions = props.challenge.paymentTransactions || [];

    return (
      <main>
        <div className="flex w-full justify-center px-4">
          <div className="flex max-w-lg flex-col justify-center px-4">
            <h1 className="mt-16 px-10 text-center text-3xl font-semibold">{title}</h1>

            <div className="mt-8">
              <div className="rounded-lg border border-warning bg-warning bg-opacity-10 p-6">
                <p className="text-warning font-semibold text-center text-lg mb-2">
                  {t("bettingClosed")}
                </p>
                <p className="text-center opacity-80">
                  {t("bettingClosedDidNotParticipate")}
                </p>
              </div>
            </div>

            {/* Betting Results Section */}
            <div className="mt-8 space-y-6">
              <div className="border-t border-base-300 pt-6">
                <h2 className="text-xl font-semibold text-center mb-6">{t("bettingResults")}</h2>

                {/* Final Outcomes */}
                <div className="mb-6">
                  <h3 className="text-lg pt-4 font-semibold mb-3">{t("finalOutcomes")}</h3>
                  <div className="space-y-2">
                    {scenarios.map((scenario, index) => {
                      const correctOutcome = scenario.correctOutcome;
                      const optionKey = `option${correctOutcome}` as keyof BettingScenario;
                      const correctOptionText = correctOutcome !== undefined
                        ? getTranslatedValue(scenario, optionKey)
                        : "Not resolved";

                      return (
                        <div key={scenario.id || index} className="rounded-lg border border-base-300 bg-base-200 bg-opacity-50 p-3">
                          <p className="font-semibold mb-1">
                            {getTranslatedValue(scenario, "scenario")}
                          </p>
                          <p className="text-sm text-neutral">
                            ✓ {correctOutcome !== undefined
                              ? `${correctOptionText}`
                              : "Not resolved"}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Team Results */}
                {bettingResults.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg pt-4 font-semibold mb-3">{t("teamResults")}</h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto pb-2">
                      {bettingResults.map((result, index) => (
                        <div
                          key={result.teamId}
                          className={`flex justify-between items-center py-3 my-3 px-4 rounded-lg border ${result.netResult > 0
                            ? 'border-base-300 shadow-sm bg-success bg-opacity-10'
                            : result.netResult < 0
                              ? 'border-base-300 shadow-sm bg-error bg-opacity-10'
                              : 'border-base-300 bg-base-200'
                            }`}
                        >
                          <div className="flex-1">
                            <p className="font-semibold">
                              {index + 1}. {result.teamName}
                            </p>
                            <p className="text-sm opacity-70">
                              {result.correctBets}/{result.totalBets} {t("correctBets")}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className={`font-semibold text-xl ${result.netResult > 0 ? 'text-green-600' : result.netResult < 0 ? 'text-error' : 'text-neutral'
                              }`}>
                              {result.netResult > 0 ? '+' : ''}${result.netResult.toFixed(0)}
                            </p>
                            <p className="text-sm opacity-70">${result.winnings.toFixed(0)}/${result.totalBetAmount}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Payment Summary */}
                {paymentTransactions.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg pt-4 font-semibold mb-3">{t("paymentSummary")}</h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {paymentTransactions.map((transaction, index) => (
                        <div key={index} className="flex justify-between items-center py-2 px-3 rounded border border-base-300 bg-base-200">
                          <p className="text-sm flex-1">
                            <span className="font-medium">{transaction.fromTeamName}</span> → <span className="font-medium">{transaction.toTeamName}</span>
                          </p>
                          <p className="font-semibold text-primary">${transaction.amount.toFixed(0)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-center">
              <button onClick={() => navigate("/challenges")} className="btn-primary btn">
                {t("backToChallenges")}
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  if (solution?.completed) {
    const bettingResults = props.challenge.bettingResults || [];
    const paymentTransactions = props.challenge.paymentTransactions || [];

    return (
      <main>
        <div className="flex w-full justify-center px-4">
          <div className="flex max-w-lg flex-col justify-center px-4">
            <h1 className="mt-16 px-10 text-center text-3xl font-semibold">{t("bettingChallengeCompleted")}</h1>
            <p className="mt-8 px-10 text-center text-lg">{t("betsPlacedMessage")}</p>

            <div className="mt-8 space-y-4">
              <h2 className="text-xl font-semibold">{t("yourBets")}</h2>
              <>
                {solution.bets.map((bet) => {
                  const scenario = scenarios.find((s) => s.id === bet.scenarioId);
                  if (!scenario) return null;

                  const optionText = getTranslatedValue(scenario, `option${bet.selectedOption}` as keyof BettingScenario);

                  return (
                    <div key={bet.scenarioId} className="rounded-lg border border-base-300 bg-base-200 bg-opacity-50 p-4">
                      <p className="font-semibold">{getTranslatedValue(scenario, "scenario")}</p>
                      <p className="text-sm text-base-content/70">
                        {t("yourBet")}: {optionText}
                      </p>
                      <p className="text-sm text-base-content/70">
                        {t("amount")}: ${bet.amount}
                      </p>
                    </div>
                  );
                })}</>
              <div className="pt-8 text-center text-lg font-semibold">
                {t("totalBetAmount")}: ${completedTotalAmount}
              </div>
            </div>

            {/* Betting Results Section */}
            {isBettingClosed && (
              <div className="mt-8 space-y-6">
                <div className="border-t border-base-300 pt-6">
                  <h2 className="text-xl font-semibold text-center mb-6">{t("bettingResults")}</h2>

                  {/* Final Outcomes */}
                  <div className="mb-6">
                    <h3 className="text-lg pt-4 font-semibold mb-3">{t("finalOutcomes")}</h3>
                    <div className="space-y-2">
                      {scenarios.map((scenario, index) => {
                        const correctOutcome = scenario.correctOutcome;
                        const optionKey = `option${correctOutcome}` as keyof BettingScenario;
                        const correctOptionText = correctOutcome !== undefined
                          ? getTranslatedValue(scenario, optionKey)
                          : "Not resolved";

                        return (
                          <div key={scenario.id || index} className="rounded-lg border border-base-300 bg-base-200 bg-opacity-50 p-3">
                            <p className="font-semibold mb-1">
                              {getTranslatedValue(scenario, "scenario")}
                            </p>
                            <p className="text-sm text-neutral">
                              ✓ {correctOutcome !== undefined
                                ? `${correctOptionText}`
                                : "Not resolved"}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Team Results */}
                  {bettingResults.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg pt-4 font-semibold mb-3">{t("teamResults")}</h3>
                      <div className="space-y-2 max-h-64 overflow-y-auto pb-2">
                        {bettingResults.map((result, index) => (
                          <div
                            key={result.teamId}
                            className={`flex justify-between items-center py-3 my-3 px-4 rounded-lg border ${result.netResult > 0
                              ? 'border-base-300 shadow-sm bg-success bg-opacity-10'
                              : result.netResult < 0
                                ? 'border-base-300 shadow-sm bg-error bg-opacity-10'
                                : 'border-base-300 bg-base-200'
                              }`}
                          >
                            <div className="flex-1">
                              <p className="font-semibold">
                                {index + 1}. {result.teamName}
                              </p>
                              <p className="text-sm opacity-70">
                                {result.correctBets}/{result.totalBets} {t("correctBets")}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className={`font-semibold text-xl ${result.netResult > 0 ? 'text-green-600' : result.netResult < 0 ? 'text-error' : 'text-neutral'
                                }`}>
                                {result.netResult > 0 ? '+' : ''}${result.netResult.toFixed(0)}
                              </p>
                              <p className="text-sm opacity-70">${result.winnings.toFixed(0)}/${result.totalBetAmount}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Payment Summary */}
                  {paymentTransactions.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg pt-4 font-semibold mb-3">{t("paymentSummary")}</h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {paymentTransactions.map((transaction, index) => (
                          <div key={index} className="flex justify-between items-center py-2 px-3 rounded border border-base-300 bg-base-200">
                            <p className="text-sm flex-1">
                              <span className="font-medium">{transaction.fromTeamName}</span> → <span className="font-medium">{transaction.toTeamName}</span>
                            </p>
                            <p className="font-semibold text-primary">${transaction.amount.toFixed(0)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!isBettingClosed && (
              <div className="mt-8 text-center">
                <div className="rounded-lg border border-warning bg-warning bg-opacity-10 p-4">
                  <p className="text-warning font-medium">{t("bettingNotClosedYet")}</p>
                  <p className="text-sm opacity-70 mt-1">{t("bettingNotClosedMessage")}</p>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-center">
              <button onClick={() => navigate("/challenges")} className="btn-primary btn">
                {t("backToChallenges")}
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <div className="flex w-full justify-center px-4">
        <div>
          <div className="flex w-full max-w-lg flex-col justify-center px-4">
            <h1 className="mt-16 px-10 text-center text-3xl font-semibold">{title}</h1>
            <p className="mt-8 px-10 text-center text-lg">{about}</p>

            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => setShowBettingModal(true)}
                className="text-sm text-primary underline hover:opacity-80"
              >
                {t("howDoesItWork")}
              </button>
            </div>

            <HowBettingWorksModal
              show={showBettingModal}
              onClose={() => setShowBettingModal(false)}
            />

            <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6">
              {scenarios.map((scenario, index) => {
                const scenarioId = scenario.id || `scenario-${index}`;
                const existingBet = existingBets[scenarioId];

                return (
                  <div key={scenarioId} className="rounded-lg border border-base-300 bg-base-200 bg-opacity-50 p-4">
                    <h3 className="mb-4 text-lg font-semibold">
                      {getTranslatedValue(scenario, "scenario")}
                    </h3>

                    <div className="mb-4 space-y-2">
                      <label className="text-sm font-medium">{t("selectOutcome")}:</label>
                      {[0, 1, 2, 3].map((optionIndex) => {
                        const optionText = getTranslatedValue(scenario, `option${optionIndex}` as keyof BettingScenario);
                        if (!optionText || optionText.trim() === "") return null;

                        return (
                          <label key={optionIndex} className="flex cursor-pointer items-center space-x-2">
                            <input
                              type="radio"
                              value={optionIndex}
                              defaultChecked={existingBet?.selectedOption === optionIndex}
                              className="radio radio-primary"
                              {...register(`${scenarioId}.selectedOption`, {
                                required: t("fieldRequired"),
                                valueAsNumber: true,
                              })}
                            />
                            <span>{optionText}</span>
                          </label>
                        );
                      })}
                    </div>

                    <Input
                      label={t("betAmount")}
                      name={`${scenarioId}.amount`}
                      type="number"
                      min="1"
                      step="1"
                      placeholder="0"
                      defaultValue={existingBet?.amount || ""}
                      required
                      requiredMessage={t("fieldRequired")}
                      errors={errors}
                      register={(name: string) => register(name, {
                        required: t("fieldRequired"),
                        valueAsNumber: true,
                        min: { value: 1, message: t("fieldRequired") },
                      })}
                      className="max-w-xs"
                    />
                  </div>
                );
              })}

              {totalAmount > 0 && (
                <div className="rounded-lg border border-primary bg-primary bg-opacity-10 p-4">
                  <p className="text-lg font-semibold">
                    {t("totalBetAmount")}: ${totalAmount}
                  </p>
                </div>
              )}

              {/* Payment URL and Email fields */}
              <div className="mt-6 space-y-4">
                <div className="rounded-lg border border-base-300 bg-base-200 bg-opacity-50 p-4">
                  <h3 className="mb-4 text-lg font-semibold">{t("paymentInformation")}</h3>

                  <div className="space-y-4">
                    <Input
                      label={tNewteam("email")}
                      name="email"
                      type="email"
                      placeholder={tNewteam("emailExample")}
                      required
                      requiredMessage={t("fieldRequired")}
                      errors={errors}
                      register={register}
                    />

                    <Input
                      label={tNewteam("bettingPaymentUrl")}
                      name="paymentUrl"
                      placeholder={tNewteam("bettingPaymentUrlExample")}
                      required
                      requiredMessage={t("fieldRequired")}
                      errors={errors}
                      register={register}
                      tooltip={tNewteam("bettingPaymentUrlTooltip")}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-center">
                <div
                  className={!canSubmit && !submitting ? "tooltip tooltip-top" : ""}
                  data-tip={!canSubmit && !submitting ? submitTooltip : ""}
                >
                  <button
                    id="submit-button"
                    type="submit"
                    disabled={submitting || !canSubmit}
                    className={`btn-primary btn ${submitting ? "loading" : ""}`}
                  >
                    {submitting ? t("placing") : t("placeBets")}
                  </button>
                </div>
              </div>
            </form>
          </div>
          <Footer />
        </div>
      </div>
    </main >
  );
};
